import React, { useState, useEffect, useRef, useCallback } from 'react';
import mqtt, { type MqttClient } from 'mqtt';
import { SENSOR_CONFIGS, SensorType, AnomalyLevel, TOPICS } from '@grosync/common';
import type { DeviceInfo } from '@grosync/common';
import { C } from '../../styles/theme';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { fetchDevices } from '../../api/devices';

// ---------------------------------------------------------------------------
// 타입
// ---------------------------------------------------------------------------

type ConnStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface LogEntry {
  id: number;
  ts: string;
  topic: string;
  payload: string;
  ok: boolean;
}

interface SensorState {
  value: string;
  anomaly: string;
}

// ---------------------------------------------------------------------------
// 상수
// ---------------------------------------------------------------------------

const WS_URL = 'ws://localhost:9001';
const INTERVALS = [
  { label: '1초', ms: 1_000 },
  { label: '5초', ms: 5_000 },
  { label: '10초', ms: 10_000 },
  { label: '30초', ms: 30_000 },
];
const ANOMALY_OPTIONS = Object.values(AnomalyLevel);

const STATUS_LABEL: Record<ConnStatus, string> = {
  disconnected: '연결 안됨',
  connecting:   '연결 중...',
  connected:    '연결됨',
  error:        '오류',
};
const STATUS_DOT: Record<ConnStatus, string> = {
  disconnected: C.text3,
  connecting:   C.warning,
  connected:    C.primary,
  error:        C.error,
};

function randomInRange(min: number, max: number): number {
  return Math.round((Math.random() * (max - min) + min) * 100) / 100;
}

// ---------------------------------------------------------------------------
// 에뮬레이터 페이지
// ---------------------------------------------------------------------------

export default function EmulatorPage(): React.ReactElement {
  const [devices, setDevices]         = useState<DeviceInfo[]>([]);
  const [selectedId, setSelectedId]   = useState('');
  const [connStatus, setConnStatus]   = useState<ConnStatus>('disconnected');
  const [sensors, setSensors]         = useState<Record<string, SensorState>>({});
  const [log, setLog]                 = useState<LogEntry[]>([]);
  const [autoOn, setAutoOn]           = useState(false);
  const [intervalMs, setIntervalMs]   = useState(5_000);
  const [msgCount, setMsgCount]       = useState(0);

  const clientRef  = useRef<MqttClient | null>(null);
  const timerRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const logIdRef   = useRef(0);
  const logEndRef  = useRef<HTMLDivElement>(null);

  // 장비 목록 로드
  useEffect(() => {
    fetchDevices().then(setDevices).catch(console.error);
  }, []);

  const device = devices.find((d) => d.id === selectedId) ?? null;

  // 장비 선택 시 센서 초기화
  useEffect(() => {
    if (!device) return;
    const init: Record<string, SensorState> = {};
    for (const st of device.sensors) {
      const cfg = SENSOR_CONFIGS[st as SensorType];
      const mid = cfg ? (cfg.validMin + cfg.validMax) / 2 : 0;
      init[st] = { value: String(Math.round(mid * 10) / 10), anomaly: AnomalyLevel.NORMAL };
    }
    setSensors(init);
  }, [device]);

  // 로그 자동 스크롤
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  // 자동 전송 타이머
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoOn && connStatus === 'connected') {
      timerRef.current = setInterval(() => sendAll(true), intervalMs);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOn, connStatus, intervalMs, sensors, device]);

  // ---------------------------------------------------------------------------
  // MQTT 연결/해제
  // ---------------------------------------------------------------------------

  const connect = useCallback(() => {
    if (!device || !device.mqttApiKey) return;
    setConnStatus('connecting');

    const client = mqtt.connect(WS_URL, {
      clientId:        device.id,
      username:        device.id,
      password:        device.mqttApiKey,
      clean:           true,
      connectTimeout:  8_000,
      reconnectPeriod: 0,   // 수동 연결만
    });

    client.on('connect', () => setConnStatus('connected'));
    client.on('error',   (err) => {
      console.error('[emulator] MQTT error:', err.message);
      setConnStatus('error');
    });
    client.on('close', () => {
      if (connStatus !== 'error') setConnStatus('disconnected');
    });

    clientRef.current = client;
  }, [device, connStatus]);

  const disconnect = useCallback(() => {
    setAutoOn(false);
    clientRef.current?.end(true);
    clientRef.current = null;
    setConnStatus('disconnected');
  }, []);

  useEffect(() => () => { clientRef.current?.end(true); }, []);

  // ---------------------------------------------------------------------------
  // 메시지 발행
  // ---------------------------------------------------------------------------

  // 단일 JSON 번들로 발행 (ADR-0008): grosync/{deviceId}/data { ts, readings }
  // deviceId는 토픽에서, unit·anomaly는 서버가 도출하므로 페이로드에 넣지 않는다.
  const publishBundle = useCallback((readings: Record<string, number>) => {
    const client = clientRef.current;
    if (!client || !device || Object.keys(readings).length === 0) return;

    const topic   = TOPICS.SENSOR_BUNDLE(device.id);
    const payload = JSON.stringify({
      ts: new Date().toISOString(),
      readings,
    });

    client.publish(topic, payload, { qos: 1 }, (err) => {
      const entry: LogEntry = {
        id:      ++logIdRef.current,
        ts:      new Date().toLocaleTimeString('ko-KR'),
        topic,
        payload,
        ok:      !err,
      };
      setLog((prev) => [...prev.slice(-199), entry]);
      if (!err) setMsgCount((n) => n + 1);
    });
  }, [device]);

  // 센서 1개의 발행값을 계산한다. randomize 시 표시값도 갱신한다.
  const resolveValue = useCallback(
    (sensorType: string, state: SensorState, randomize: boolean): number | null => {
      const cfg = SENSOR_CONFIGS[sensorType as SensorType];
      const value = randomize && cfg
        ? randomInRange(cfg.validMin, cfg.validMax)
        : parseFloat(state.value);
      if (isNaN(value)) return null;
      if (randomize && cfg) {
        setSensors((prev) => ({
          ...prev,
          [sensorType]: { ...prev[sensorType], value: String(value) },
        }));
      }
      return value;
    },
    [],
  );

  const publish = useCallback((sensorType: string, state: SensorState, randomize = false) => {
    const value = resolveValue(sensorType, state, randomize);
    if (value === null) return;
    publishBundle({ [sensorType]: value });
  }, [resolveValue, publishBundle]);

  const sendAll = useCallback((randomize = false) => {
    if (!device) return;
    const readings: Record<string, number> = {};
    for (const st of device.sensors) {
      const state = sensors[st];
      if (!state) continue;
      const value = resolveValue(st, state, randomize);
      if (value !== null) readings[st] = value;
    }
    publishBundle(readings);
  }, [device, sensors, resolveValue, publishBundle]);

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  const canConnect   = !!device && !!device.mqttApiKey && connStatus === 'disconnected';
  const isConnected  = connStatus === 'connected';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 16 }}>
      <PageHeader title="MQTT 에뮬레이터" subtitle="장비 센서 데이터를 수동으로 발행하여 시스템을 테스트합니다" />

      {/* ── 연결 패널 ─────────────────────────────────────────────────── */}
      <div style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        {/* 장비 선택 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 240 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: C.text2 }}>시뮬레이션 장비</label>
          <select
            value={selectedId}
            onChange={(e) => { disconnect(); setSelectedId(e.target.value); }}
            disabled={isConnected}
            style={{
              padding: '7px 10px',
              background: C.inputBg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text1,
              fontSize: 13,
              outline: 'none',
            }}
          >
            <option value="">장비를 선택하세요</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.type}) {!d.mqttApiKey ? '— API Key 없음' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* 브로커 URL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: C.text2 }}>브로커 (WebSocket)</label>
          <span style={{ fontSize: 13, color: C.text1, fontFamily: 'monospace', padding: '7px 0' }}>{WS_URL}</span>
        </div>

        {/* 연결 상태 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: STATUS_DOT[connStatus],
            boxShadow: isConnected ? `0 0 6px ${C.primary}` : 'none',
            transition: 'all .3s',
          }} />
          <span style={{ fontSize: 13, color: C.text2 }}>{STATUS_LABEL[connStatus]}</span>
          {isConnected && (
            <span style={{ fontSize: 11, color: C.text3, marginLeft: 4 }}>
              발행 {msgCount}건
            </span>
          )}
        </div>

        {/* 연결 버튼 */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isConnected ? (
            <Button onClick={connect} disabled={!canConnect} loading={connStatus === 'connecting'}>
              연결
            </Button>
          ) : (
            <Button variant="danger" onClick={disconnect}>연결 해제</Button>
          )}
        </div>
      </div>

      {/* ── 센서 + 로그 ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0 }}>

        {/* 센서 패널 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>

          {/* 자동전송 툴바 */}
          <div style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text2 }}>자동 전송</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {INTERVALS.map(({ label, ms }) => (
                <button
                  key={ms}
                  onClick={() => setIntervalMs(ms)}
                  disabled={!isConnected}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: `1px solid ${intervalMs === ms ? C.primary : C.border}`,
                    background: intervalMs === ms ? C.primary + '18' : 'transparent',
                    color: intervalMs === ms ? C.primary : C.text2,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isConnected ? 'pointer' : 'not-allowed' }}>
              <div
                onClick={() => isConnected && setAutoOn((v) => !v)}
                style={{
                  width: 36, height: 20, borderRadius: 10,
                  background: autoOn && isConnected ? C.primary : C.border,
                  position: 'relative',
                  transition: 'background .2s',
                  cursor: isConnected ? 'pointer' : 'not-allowed',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 3, left: autoOn && isConnected ? 18 : 3,
                  width: 14, height: 14, borderRadius: '50%',
                  background: C.white,
                  transition: 'left .2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <span style={{ fontSize: 12, color: autoOn && isConnected ? C.primary : C.text2 }}>
                {autoOn && isConnected ? '전송 중' : '중지'}
              </span>
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button size="sm" variant="ghost" onClick={() => sendAll(true)} disabled={!isConnected || !device}>
                랜덤값 전송
              </Button>
              <Button size="sm" onClick={() => sendAll(false)} disabled={!isConnected || !device}>
                전체 전송
              </Button>
            </div>
          </div>

          {/* 센서 카드 목록 */}
          {!device ? (
            <div style={{ textAlign: 'center', color: C.text3, fontSize: 13, padding: 40 }}>
              장비를 선택하면 센서 목록이 표시됩니다.
            </div>
          ) : device.sensors.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.text3, fontSize: 13, padding: 40 }}>
              이 장비에 등록된 센서가 없습니다.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {device.sensors.map((st) => {
                const cfg = SENSOR_CONFIGS[st as SensorType];
                const state = sensors[st] ?? { value: '0', anomaly: AnomalyLevel.NORMAL };
                const anomalyColor = state.anomaly === 'normal' ? C.primary
                  : state.anomaly === 'warning' ? C.warning : C.error;

                return (
                  <div key={st} style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: '14px 16px',
                  }}>
                    {/* 헤더 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text1 }}>{st}</div>
                        <div style={{ fontSize: 11, color: C.text3 }}>{cfg?.description ?? ''}</div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        padding: '2px 8px', borderRadius: 10,
                        background: anomalyColor + '18', color: anomalyColor,
                      }}>
                        {state.anomaly}
                      </span>
                    </div>

                    {/* 값 입력 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <input
                        type="number"
                        value={state.value}
                        onChange={(e) => setSensors((prev) => ({
                          ...prev,
                          [st]: { ...prev[st], value: e.target.value },
                        }))}
                        min={cfg?.validMin}
                        max={cfg?.validMax}
                        step="0.1"
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          background: C.inputBg,
                          border: `1px solid ${C.border}`,
                          borderRadius: 6,
                          color: C.text1,
                          fontSize: 14,
                          fontWeight: 600,
                          outline: 'none',
                          textAlign: 'right',
                        }}
                      />
                      <span style={{ fontSize: 12, color: C.text3, minWidth: 40 }}>{cfg?.unit ?? ''}</span>
                    </div>

                    {/* 이상치 선택 */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                      {ANOMALY_OPTIONS.map((a) => {
                        const c = a === 'normal' ? C.primary : a === 'warning' ? C.warning : C.error;
                        const isSelected = state.anomaly === a;
                        return (
                          <button
                            key={a}
                            onClick={() => setSensors((prev) => ({
                              ...prev,
                              [st]: { ...prev[st], anomaly: a },
                            }))}
                            style={{
                              flex: 1,
                              padding: '4px 0',
                              borderRadius: 6,
                              border: `1px solid ${isSelected ? c : C.border}`,
                              background: isSelected ? c + '18' : 'transparent',
                              color: isSelected ? c : C.text3,
                              fontSize: 11,
                              fontWeight: isSelected ? 600 : 400,
                              cursor: 'pointer',
                            }}
                          >
                            {a}
                          </button>
                        );
                      })}
                    </div>

                    {/* 개별 전송 */}
                    <Button
                      size="sm"
                      style={{ width: '100%' }}
                      disabled={!isConnected}
                      onClick={() => publish(st, state)}
                    >
                      전송
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 로그 패널 */}
        <div style={{
          width: 360,
          flexShrink: 0,
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text1 }}>발행 로그</span>
            <button
              onClick={() => setLog([])}
              style={{ background: 'none', border: 'none', fontSize: 11, color: C.text3, cursor: 'pointer' }}
            >
              지우기
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, fontFamily: 'monospace', fontSize: 11 }}>
            {log.length === 0 ? (
              <div style={{ textAlign: 'center', color: C.text3, padding: '24px 0' }}>메시지 없음</div>
            ) : (
              log.map((entry) => (
                <div key={entry.id} style={{
                  marginBottom: 8,
                  padding: '6px 8px',
                  borderRadius: 6,
                  background: entry.ok ? C.primaryLight : C.errorLight,
                  borderLeft: `3px solid ${entry.ok ? C.primary : C.error}`,
                }}>
                  <div style={{ color: C.text3, marginBottom: 2 }}>{entry.ts}</div>
                  <div style={{ color: C.primary, fontWeight: 600, marginBottom: 2, wordBreak: 'break-all' }}>
                    {entry.topic}
                  </div>
                  <div style={{ color: C.text2, wordBreak: 'break-all' }}>{entry.payload}</div>
                  {!entry.ok && <div style={{ color: C.error, marginTop: 2 }}>전송 실패</div>}
                </div>
              ))
            )}
            <div ref={logEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
